import { useCallback, useMemo, useState } from "react";

import { Button } from "@/components/ui/button/Button";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "@/components/ui/dialog/Dialog";
import FormInput from "@/components/ui/form-input";

type SaveMode = "rename" | "copy";

export function GuidedReviewSaveMenu({
  currentTitle,
  disabled = false,
  onRename,
  onSaveCopy,
}: {
  currentTitle?: string;
  disabled?: boolean;
  onRename: (title: string) => Promise<boolean>;
  onSaveCopy: (title: string) => Promise<boolean>;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mode, setMode] = useState<SaveMode>("rename");
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openDialog = useCallback((nextMode: SaveMode) => {
    setMode(nextMode);
    setTitle(nextMode === "rename" ? (currentTitle ?? "").trim() : "");
    setError(null);
    setDialogOpen(true);
  }, [currentTitle]);

  const dropdownItems = useMemo(
    () => [
      {
        name: "Rename current review",
        onClick: () => openDialog("rename"),
      },
    ],
    [openDialog],
  );

  const handleSubmit = async () => {
    const trimmed = title.trim();
    if (!trimmed) {
      setError("Enter a title");
      return;
    }
    setSaving(true);
    setError(null);
    const ok = mode === "rename" ? await onRename(trimmed) : await onSaveCopy(trimmed);
    setSaving(false);
    if (ok) setDialogOpen(false);
    else if (!error) setError("Could not save");
  };

  return (
    <>
      <div data-testid="guided-review-save-menu">
        <Button
          outline
          type="button"
          disabled={disabled}
          onClick={() => openDialog("copy")}
          data-testid="guided-review-save"
          dropdownItems={dropdownItems}
          dropdownMenuButtonAriaLabel="More save options"
        >
          Save
        </Button>
      </div>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} size="md">
        <DialogTitle>{mode === "rename" ? "Rename current review" : "Save copy as"}</DialogTitle>
        <DialogBody>
          <p className="mb-3 text-sm text-slate-600 dark:text-slate-300">
            {mode === "rename"
              ? "Name this review. Your work continues to auto-save here."
              : "Save a named snapshot. You keep editing the current review."}
          </p>
          <FormInput
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Review title"
            maxLength={120}
            data-testid="guided-review-title-input"
          />
          {error ? <p className="mt-2 text-sm text-rose-600">{error}</p> : null}
        </DialogBody>
        <DialogActions>
          <Button color="slate" type="button" onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          <Button color="indigo" type="button" loading={saving} onClick={() => void handleSubmit()}>
            {mode === "rename" ? "Save" : "Save copy"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
